const _ = require('lodash');

import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { GROUPS_NAME, STATUS } from '../../util/userHelpers';
import { fetchApiTeamTransactions } from '../../util/api';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  queryParams: {},
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchOrdersOrSalesError: null,
        queryParams: payload.queryParams,
      };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = queryParams => ({
  type: FETCH_ORDERS_OR_SALES_REQUEST,
  payload: { queryParams },
});
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;
const INBOX_SEARCH_PAGE_SIZE = 100;

const getQueryParams = (onlyFilter, search) => {
  const { page = 1 } = parse(search);

  return {
    only: onlyFilter,
    lastTransitions: getAllTransitionsForEveryProcess(),
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };
};

const entityKey = entity => `${entity.type}:${entity.id.uuid}`;

export const mergeTransactionResponses = responses => {
  const dataById = new Map();
  const includedById = new Map();

  responses.filter(Boolean).forEach(response => {
    (response?.data?.data || []).forEach(entity => dataById.set(entityKey(entity), entity));
    (response?.data?.included || []).forEach(entity => includedById.set(entityKey(entity), entity));
  });

  const data = Array.from(dataById.values());
  return {
    data: {
      data,
      included: Array.from(includedById.values()),
      meta: {
        totalItems: data.length,
        totalPages: 1,
        page: 1,
        perPage: Math.max(data.length, 1),
      },
    },
  };
};

export const fetchAllTransactionPages = async fetchPage => {
  const firstResponse = await fetchPage(1);
  const totalPages = firstResponse?.data?.meta?.totalPages || 1;
  const remainingResponses = await Promise.all(
    Array.from({ length: Math.max(totalPages - 1, 0) }, (_, index) => fetchPage(index + 2))
  );

  return mergeTransactionResponses([firstResponse, ...remainingResponses]);
};

export const loadTeamData = (response, params, search, onlyFilter, currentUser, group) => (
  dispatch,
  getState,
  sdk
) => {
  let apiQueryParams = getQueryParams(onlyFilter, search);
  let body = {
    apiQueryParams,
    userId: currentUser.id.uuid,
    ownerId: group.id,
  };

  return fetchApiTeamTransactions(body)
    .then(res => {
      let originalData = response?.data?.data || [];
      let originalIncluded = response?.data?.included || [];
      let externalData = res?.data?.data || [];
      let externalIncluded = res?.data?.included || [];

      let originalMeta = response?.data?.meta || [];
      let externalMeta = res?.data?.meta || [];

      response.data.data = _.concat(originalData, externalData);
      response.data.included = _.concat(originalIncluded, externalIncluded);

      response.data.meta = {
        totalItems: originalMeta.totalItems + externalMeta.totalItems,
        totalPages: Math.max(originalMeta.totalPages, externalMeta.totalPages),
        page: Math.max(originalMeta.page, externalMeta.page),
        perPage: originalMeta.perPage,
      };

      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  const parsedSearch = parse(search);
  const keywords = String(parsedSearch.keywords || '').trim();
  dispatch(fetchOrdersOrSalesRequest({ ...parsedSearch, keywords }));

  const apiQueryParams = getQueryParams(onlyFilter, search);

  if (keywords) {
    const searchQueryParams = {
      ...apiQueryParams,
      page: 1,
      perPage: INBOX_SEARCH_PAGE_SIZE,
    };

    return fetchAllTransactionPages(page => sdk.transactions.query({ ...searchQueryParams, page }))
      .then(async directResponse => {
        let currentUser = getState()?.user?.currentUser;
        if (!currentUser) {
          const currentUserResponse = await sdk.currentUser.show();
          currentUser = currentUserResponse?.data?.data;
        }

        const groups = currentUser?.attributes?.profile?.publicData?.[GROUPS_NAME] || [];
        const activeGroup = groups.find(group => group.status == STATUS.ACTIVE);
        let response = directResponse;

        if (tab == 'sales' && activeGroup) {
          const teamResponse = await fetchAllTransactionPages(page =>
            fetchApiTeamTransactions({
              apiQueryParams: { ...searchQueryParams, page },
              userId: currentUser.id.uuid,
              ownerId: activeGroup.id,
            })
          );
          response = mergeTransactionResponses([directResponse, teamResponse]);
        }

        dispatch(addMarketplaceEntities(response));
        dispatch(fetchOrdersOrSalesSuccess(response));
        return response;
      })
      .catch(e => {
        dispatch(fetchOrdersOrSalesError(storableError(e)));
        throw e;
      });
  }

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      let state = getState();
      let currentUser = state?.user?.currentUser || {};
      let currentUserPublicData = currentUser?.attributes?.profile?.publicData || {};
      let groups = currentUserPublicData[GROUPS_NAME] || [];

      let activeGroup = groups.find(g => g.status == STATUS.ACTIVE);

      if (tab == 'sales' && !!activeGroup) {
        dispatch(loadTeamData(response, params, search, onlyFilter, currentUser, activeGroup));
      } else {
        dispatch(addMarketplaceEntities(response));
        dispatch(fetchOrdersOrSalesSuccess(response));
      }

      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
